import React from 'react';
import styled from 'styled-components';
import { FaPlus, FaTimes } from 'react-icons/fa';

interface ContactTabsProps {
  contacts: Array<{ name?: string; firstName?: string; [key: string]: any }>;
  activeIndex: number;
  onTabChange: (index: number) => void;
  onAddContact: () => void;
  onRemoveContact: (index: number) => void;
  maxContacts?: number;
}

const TabsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 8px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  padding-bottom: 2px;
`;

const Tab = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  font-size: 0.75rem;
  font-weight: ${({ $active }) => $active ? '600' : '400'};
  color: ${({ theme, $active }) => $active ? theme.colors.primary : theme.colors.text};
  background-color: ${({ theme, $active }) => $active ? theme.colors.backgroundHover : 'transparent'};
  border: none;
  border-bottom: 2px solid ${({ theme, $active }) => $active ? theme.colors.primary : 'transparent'};
  cursor: pointer;
  transition: all 0.2s;
  border-radius: 4px 4px 0 0;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.backgroundHover};
  }
  
  @media (max-width: 768px) {
    padding: 5px 8px;
    font-size: 0.7rem;
    gap: 4px;
  }
  
  /* Empêcher la soumission du formulaire */
  &[type="button"] {
    -webkit-appearance: button;
  }
`;

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.error};
  cursor: pointer;
  font-size: 10px;
  transition: transform 0.2s;
  
  &:hover {
    transform: scale(1.2);
  }
`;

const AddButton = styled.button<{ $disabled: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  font-size: 12px;
  color: ${({ theme, $disabled }) => $disabled ? theme.colors.textLight : theme.colors.primary};
  background-color: ${({ theme }) => theme.colors.backgroundHover};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  cursor: ${({ $disabled }) => $disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s;
  
  &:hover {
    background-color: ${({ theme, $disabled }) => !$disabled && theme.colors.primary};
    color: ${({ $disabled }) => !$disabled && 'white'};
    transform: ${({ $disabled }) => !$disabled && 'scale(1.1)'};
  }
  
  @media (max-width: 768px) {
    width: 26px;
    height: 26px;
    font-size: 11px;
  }
`;

/**
 * Capitalise la première lettre d'une chaîne
 */
const capitalizeFirstLetter = (str: string): string => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Génère le label d'un onglet contact : "Nom + 1ère lettre prénom"
 * Ex: "Dupont M", "Martin J"
 */
const getContactLabel = (contact: { name?: string; firstName?: string }, index: number): string => {
  const name = contact.name?.trim() || '';
  const firstName = contact.firstName?.trim() || '';
  
  if (name && firstName) {
    return `${capitalizeFirstLetter(name)} ${firstName.charAt(0).toUpperCase()}`;
  }
  if (name) {
    return capitalizeFirstLetter(name);
  }
  if (firstName) {
    return capitalizeFirstLetter(firstName);
  }
  
  return `Contact ${index + 1}`;
};

/**
 * Composant d'onglets pour gérer plusieurs contacts
 * - 1er contact obligatoire (pas de suppression)
 * - Maximum 3 contacts
 * - Nommage : "Nom + 1ère lettre prénom"
 */
export const ContactTabs: React.FC<ContactTabsProps> = ({
  contacts,
  activeIndex,
  onTabChange,
  onAddContact,
  onRemoveContact,
  maxContacts = 3
}) => {
  const canAddMore = contacts.length < maxContacts;

  return (
    <TabsContainer>
      {contacts.map((contact, index) => (
        <Tab
          key={index}
          type="button"
          $active={activeIndex === index}
          onClick={(e) => {
            e.preventDefault();
            onTabChange(index);
          }}
        >
          {getContactLabel(contact, index)}
          
          {/* Bouton de suppression (sauf pour le 1er contact) */}
          {index > 0 && (
            <RemoveButton
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onRemoveContact(index);
              }}
              title="Supprimer ce contact"
            >
              <FaTimes />
            </RemoveButton>
          )}
        </Tab>
      ))}
      
      {/* Bouton d'ajout */}
      <AddButton
        type="button"
        $disabled={!canAddMore}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onAddContact();
        }}
        disabled={!canAddMore}
        title={canAddMore ? 'Ajouter un contact' : `Maximum ${maxContacts} contacts`}
      >
        <FaPlus />
      </AddButton>
    </TabsContainer>
  );
};
